import { Check } from 'lucide-react';
import { Button, Card } from '@vnetwork/ui/primitives';
import type { JourneyNote, JourneyNegotiation } from '@/api/customer-journey';
import type { StageStatus } from './board-helpers';
import { StageNotes } from './stage-notes';
import { StageActions } from './stage-actions';
import { PocServiceCard } from './poc-service-card';
import { BusinessTripsSection, NegotiationsSection, ContractServicesSection } from './stage-sections';

/** Badge background per column status. */
const STATUS_BG: Record<StageStatus, string> = {
  completed: 'var(--vn-success-solid)',
  current: 'var(--vn-info-solid)',
  pending: 'var(--vn-bg-muted)',
  disabled: 'var(--vn-bg-muted)',
};

export interface StageColumnData {
  step: string;
  label: string;
  index: number;
  status: StageStatus;
  notes: JourneyNote[];
  pocs: JourneyNote[];
  businesses: JourneyNote[];
  tickets: JourneyNote[];
  negotiations: JourneyNegotiation[];
  contractServices: JourneyNote[];
}

/**
 * One board column (pipeline step). The header is clickable to move the journey
 * here (active journeys only); the body stacks step notes, any step-specific
 * sections (POC cards / business trips / contract services), negotiations, and
 * the action rail. Mirrors legacy StepColumn.
 */
export function StageColumn({
  data,
  interactive,
  locked,
  hasCompletedContract,
  convertingPocId,
  onMoveToStep,
  onAddNote,
  onAddNegotiation,
  onDeleteNote,
  onConvertPoc,
  onUpdatePocResult,
  onExtendPoc,
  onCreateBusinessTrip,
  onCreateMeeting,
  onCreatePOC,
  onCreateContract,
}: {
  data: StageColumnData;
  interactive: boolean;
  locked: boolean;
  hasCompletedContract: boolean;
  convertingPocId: number | null;
  onMoveToStep: (step: string) => void;
  onAddNote: (step: string) => void;
  onAddNegotiation: (step: string) => void;
  onDeleteNote: (note: JourneyNote) => void;
  onConvertPoc: (note: JourneyNote) => void;
  onUpdatePocResult: (note: JourneyNote) => void;
  onExtendPoc: (note: JourneyNote) => void;
  onCreateBusinessTrip: () => void;
  onCreateMeeting: () => void;
  onCreatePOC: () => void;
  onCreateContract: () => void;
}) {
  const { step, label, index, status } = data;
  const canClickHeader = interactive && status !== 'current';
  const pocActive = interactive && !locked;

  return (
    <Card
      flat
      style={{
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        gap: 'var(--vn-space-2)',
        minWidth: 0,
        padding: 'var(--vn-space-2)',
        border: status === 'current' ? '1px dashed var(--vn-info-solid)' : '1px solid var(--vn-border-subtle)',
        background: 'var(--vn-bg-subtle)',
      }}
    >
      {/* Header */}
      <Button
        type="button"
        variant="ghost"
        disabled={!canClickHeader}
        onClick={() => canClickHeader && onMoveToStep(step)}
        title={canClickHeader ? `Move journey to "${label}"` : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--vn-space-2)',
          width: '100%',
          textAlign: 'left',
          padding: 'var(--vn-space-1)',
          cursor: canClickHeader ? 'pointer' : 'default',
          height: 'auto',
          justifyContent: 'flex-start',
        }}
      >
        <span
          aria-hidden
          style={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            fontSize: 10,
            fontWeight: 'var(--vn-font-weight-bold)',
            background: STATUS_BG[status],
            color: status === 'pending' || status === 'disabled' ? 'var(--vn-fg-muted)' : 'var(--vn-fg-on-solid)',
          }}
        >
          {status === 'completed' ? <Check size={12} strokeWidth={3} /> : index + 1}
        </span>
        <span
          style={{
            fontSize: 'var(--vn-font-size-sm)',
            fontWeight: 'var(--vn-font-weight-medium)',
            color: status === 'current' ? 'var(--vn-info-text)' : 'var(--vn-fg-default)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
      </Button>

      {/* Body */}
      <div style={{ display: 'grid', gap: 'var(--vn-space-2)', alignContent: 'start' }}>
        <StageNotes notes={data.notes} canDelete={interactive} onDelete={onDeleteNote} />

        {step === 'poc' && data.pocs.length ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
            {data.pocs.map((note) => (
              <PocServiceCard
                key={note.customer_journey_note_id}
                note={note}
                canAct={pocActive}
                converting={convertingPocId === Number(note.service_poc?.service_id)}
                onConvert={onConvertPoc}
                onUpdateResult={onUpdatePocResult}
                onExtend={onExtendPoc}
              />
            ))}
          </div>
        ) : null}

        {step === 'presentation' ? (
          <BusinessTripsSection businesses={data.businesses} tickets={data.tickets} />
        ) : null}

        <NegotiationsSection negotiations={data.negotiations} />

        {step === 'contract' ? <ContractServicesSection services={data.contractServices} /> : null}

        {interactive ? (
          <StageActions
            step={step}
            disabled={locked}
            hasCompletedContract={hasCompletedContract}
            onAddNote={() => onAddNote(step)}
            onAddNegotiation={() => onAddNegotiation(step)}
            onCreateBusinessTrip={onCreateBusinessTrip}
            onCreateMeeting={onCreateMeeting}
            onCreatePOC={onCreatePOC}
            onCreateContract={onCreateContract}
          />
        ) : null}
      </div>
    </Card>
  );
}

export default StageColumn;
